import { AWXConnectedAccountStatus, type AwxConnectedAccount } from '@ankor-io/common/types/awx'
import type { ModuleInfo } from '@ankor-io/common/types/pay'

export enum ModuleKYCState {
  /** Module activation is available */
  CAN_ACTIVATE = 'CAN_ACTIVATE',
  /** Connected account created, KYC in progress */
  PENDING = 'PENDING',
  /** Additional information needed from user */
  ACTION_REQUIRED = 'ACTION_REQUIRED',
  /** KYC submitted, under review */
  IN_REVIEW = 'IN_REVIEW',
  /** Fully active */
  ACTIVE = 'ACTIVE',
  /** Account suspended */
  SUSPENDED = 'SUSPENDED',
}

export type ModuleCTAContent = {
  label: string
  to?: string
  /** When true, Layout renders this CTA as a button with an onClick handler */
  activateModule?: boolean
}

export type ModuleBannerContent = {
  variant: 'info' | 'warning' | 'error' | 'success'
  title: string
  description: string
}

export type ModuleKYCContent = {
  /** CTA shown after the section header. null = no CTA */
  cta: ModuleCTAContent | null
  /** Tooltip for disabled items in this section */
  tooltip: string | null
  /** Banner for the dashboard page */
  banner: ModuleBannerContent | null
  /** Whether the dashboard menu item is accessible */
  dashboardEnabled: boolean
  /** Whether non-dashboard menu items are accessible */
  menuItemsEnabled: boolean
}

const stateContent: Record<ModuleKYCState, ModuleKYCContent> = {
  [ModuleKYCState.CAN_ACTIVATE]: {
    cta: { label: 'Activate module', activateModule: true },
    tooltip: 'Activate this module to enable this feature',
    banner: {
      variant: 'info',
      title: 'Module Ready to Activate',
      description: 'Your account setup is complete. Activate this module to start using all features.',
    },
    dashboardEnabled: false,
    menuItemsEnabled: false,
  },
  [ModuleKYCState.PENDING]: {
    cta: { label: 'Complete KYC', to: '/kyc-rfi' },
    tooltip: 'Complete your KYC to enable this feature',
    banner: {
      variant: 'info',
      title: 'Complete Your Application',
      description: "You're almost there. Finish providing your business details to activate your vessel(s).",
    },
    dashboardEnabled: true,
    menuItemsEnabled: false,
  },
  [ModuleKYCState.ACTION_REQUIRED]: {
    cta: { label: 'Review KYC information', to: '/kyc-rfi' },
    tooltip: 'Review your KYC to activate this feature',
    banner: {
      variant: 'warning',
      title: 'Action Required: Additional Information Needed',
      description:
        'Our team needs clarification on a few details. Please review and respond to complete your activation.',
    },
    dashboardEnabled: true,
    menuItemsEnabled: false,
  },
  [ModuleKYCState.IN_REVIEW]: {
    cta: { label: 'Reviewing your information' },
    tooltip: 'Features will be enabled once your application is approved',
    banner: {
      variant: 'info',
      title: 'Activation is in progress',
      description:
        'Thanks for submitting your details. Our team is currently reviewing your information. No action needed right now.',
    },
    dashboardEnabled: true,
    menuItemsEnabled: false,
  },
  [ModuleKYCState.ACTIVE]: {
    cta: null,
    tooltip: null,
    banner: null,
    dashboardEnabled: true,
    menuItemsEnabled: true,
  },
  [ModuleKYCState.SUSPENDED]: {
    cta: null,
    tooltip: 'This feature is currently suspended',
    banner: {
      variant: 'error',
      title: 'Account Suspended',
      description: 'Your account has been suspended. Please contact support for assistance.',
    },
    dashboardEnabled: true,
    menuItemsEnabled: false,
  },
}

const statusToState: Partial<Record<AWXConnectedAccountStatus, ModuleKYCState>> = {
  [AWXConnectedAccountStatus.CREATED]: ModuleKYCState.PENDING,
  [AWXConnectedAccountStatus.ACTION_REQUIRED]: ModuleKYCState.ACTION_REQUIRED,
  [AWXConnectedAccountStatus.SUBMITTED]: ModuleKYCState.IN_REVIEW,
  [AWXConnectedAccountStatus.SUSPENDED]: ModuleKYCState.SUSPENDED,
}

/**
 * Derives KYC state from module availability and connected account.
 * With no connected account and activation disabled, returns null; getModuleContent still returns label-only content.
 */
export function deriveModuleKYCState(
  moduleAvailability: ModuleInfo | null,
  connectedAccount: AwxConnectedAccount | null,
): ModuleKYCState | null {
  if (!connectedAccount) {
    return moduleAvailability?.activation === 'enabled' ? ModuleKYCState.CAN_ACTIVATE : null
  }
  if (connectedAccount.status === AWXConnectedAccountStatus.ACTIVE) {
    return moduleAvailability?.activation === 'enabled'
      ? ModuleKYCState.CAN_ACTIVATE
      : ModuleKYCState.ACTIVE
  }
  return statusToState[connectedAccount.status] ?? ModuleKYCState.PENDING
}

const defaultContent: ModuleKYCContent = {
  cta: null,
  tooltip: null,
  banner: null,
  dashboardEnabled: false,
  menuItemsEnabled: false,
}

/** Shown when module is in list but activation disabled (e.g. complete other module's KYC first). CTA is label-only. */
const activationDisabledContent: ModuleKYCContent = {
  ...defaultContent,
  cta: { label: 'Complete KYC to continue' },
  tooltip: "Complete KYC to activate this module",
}

/**
 * Returns sidebar and banner content for a module. When there is no connected account and
 * activation is disabled, returns label-only "Complete KYC" (no link).
 */
export function getModuleContent(
  moduleAvailability: ModuleInfo | null,
  connectedAccount: AwxConnectedAccount | null,
): ModuleKYCContent {
  const state = deriveModuleKYCState(moduleAvailability, connectedAccount)
  if (state) return stateContent[state]

  if (!connectedAccount && moduleAvailability?.activation === 'disabled') {
    return activationDisabledContent
  }

  return defaultContent
}
